"use client";

import React, { useState } from "react";
import { Sparkles, MessageSquare, Phone, PhoneCall, AlertTriangle, Zap } from "lucide-react";
import { UserSubscriptionSummary } from "@/lib/quotas";

interface QuotaUsageBadgeProps {
  subscription: UserSubscriptionSummary | null;
  onOpenUpgrade: () => void;
}

export default function QuotaUsageBadge({ subscription, onOpenUpgrade }: QuotaUsageBadgeProps) {
  const [showPopover, setShowPopover] = useState(false);

  if (!subscription) return null;

  const isTrial = subscription.isTrial;
  const isExpired = subscription.isExpired;
  const days = subscription.daysRemaining;

  const wa = subscription.quotas.whatsapp;
  const sms = subscription.quotas.sms;
  const call = subscription.quotas.call;

  const isNearLimit = wa.percent >= 80 || sms.percent >= 80 || call.percent >= 80;

  return (
    <div className="relative inline-block">
      {/* Trigger Pill */}
      <button
        type="button"
        onClick={() => setShowPopover(!showPopover)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer ${
          isExpired
            ? "bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400 animate-pulse"
            : isNearLimit
            ? "bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400"
            : "bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400 hover:border-cyan-500/40"
        }`}
      >
        {isExpired ? (
          <AlertTriangle className="w-3.5 h-3.5" />
        ) : (
          <Sparkles className="w-3.5 h-3.5 text-cyan-500" />
        )}
        <span>
          {isExpired
            ? "Trial Expired (Upgrade Required)"
            : isTrial
            ? `7-Day Free Trial: ${days}d left`
            : `${subscription.planTier.toUpperCase()} Active`}
        </span>
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
      </button>

      {/* Popover Card */}
      {showPopover && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setShowPopover(false)}
          />
          <div className="absolute right-0 mt-2 w-72 p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl z-40 animate-in fade-in zoom-in-95 text-xs">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <div>
                <h4 className="font-bold text-slate-900 dark:text-white">
                  {isTrial ? "Tier 1 Trial Quotas" : `${subscription.planTier.toUpperCase()} Plan`}
                </h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {isExpired ? "Trial expired" : `${days} calendar days remaining`}
                </p>
              </div>
              <span className="px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 font-extrabold text-[10px] uppercase">
                {subscription.planTier}
              </span>
            </div>

            {/* Quota Progress Meters */}
            <div className="py-3 space-y-2.5">
              {/* WhatsApp */}
              <div>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                    <MessageSquare className="w-3 h-3 text-emerald-500" /> WhatsApp Sends
                  </span>
                  <span className="font-bold">
                    {wa.used} / {wa.limit}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      wa.percent >= 90 ? "bg-red-500" : wa.percent >= 75 ? "bg-amber-500" : "bg-emerald-500"
                    }`}
                    style={{ width: `${wa.percent}%` }}
                  />
                </div>
              </div>

              {/* SMS */}
              <div>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                    <Phone className="w-3 h-3 text-blue-500" /> Transactional SMS
                  </span>
                  <span className="font-bold">
                    {sms.used} / {sms.limit}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      sms.percent >= 90 ? "bg-red-500" : sms.percent >= 75 ? "bg-amber-500" : "bg-blue-500"
                    }`}
                    style={{ width: `${sms.percent}%` }}
                  />
                </div>
              </div>

              {/* AI Voice Calls */}
              <div>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                    <PhoneCall className="w-3 h-3 text-cyan-500" /> AI Calls (min)
                  </span>
                  <span className="font-bold">
                    {call.used} / {call.limit}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      call.percent >= 90 ? "bg-red-500" : call.percent >= 75 ? "bg-amber-500" : "bg-cyan-500"
                    }`}
                    style={{ width: `${call.percent}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Upgrade CTA */}
            <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  setShowPopover(false);
                  onOpenUpgrade();
                }}
                className="w-full py-2 px-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md hover:brightness-110 transition-all cursor-pointer"
              >
                <Zap className="w-3.5 h-3.5" />
                <span>Upgrade / Renew Plan</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
